import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[] = [];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    currentLang: string;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private translate: TranslateService,
    ) {}


        private readonly fakeNotifications: Notification[] = [
            {
                viajeId: 'C4BC5F',
                proceso: 'Cancelación de reservación',
                mensaje: 'Se ha cancelado la reservación C4BC5F.',
                fecha: '11/09/2025 09:55:00',
            },
            {
                viajeId: 'C4BC5F',
                proceso: 'Reasignación de conductor',
                mensaje:
                    'Nuevo conductor asignado: Roberto Perales Valtierra. Vehículo: NISSAN V DRIVE 2023 VERSA 77HB1X',
                fecha: '11/09/2025 09:50:00',
            },
            {
                viajeId: 'C4BC5F',
                proceso: 'Reasignación de viaje',
                mensaje: 'Su viaje C4BC5F ha sido reasignado a otro conductor.',
                fecha: '11/09/2025 09:49:00',
            },
            {
                viajeId: 'C4BC5F',
                proceso: 'Asignación de conductor',
                mensaje:
                    'Conductor asignado: Roberto Gutiérrez Hernández. Vehículo: TOYOTA COROLLA 2020 SEDAN 59HA9X.',
                fecha: '11/09/2025 09:47:00',
            },
            {
                viajeId: 'C4BC5F',
                proceso: 'Viaje pagado',
                mensaje: 'Se ha registrado el pago de su viaje C4BC5F.',
                fecha: '11/09/2025 09:46:00',
            },
            {
                viajeId: 'C4BC5F',
                proceso: 'Nueva reservación',
                mensaje: 'Se ha creado una nueva reservación: Folio C4BC5F. Fecha: 09/11/2025 09:45 am.',
                fecha: '11/09/2025 09:45:00',
            },
        ];

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

        this.initializeLanguage();

        //this.notifications = this.fakeNotifications;

        this._notificationsService.get_notifications().subscribe({
            next: (data: Notification[]) => {
                // Si la API devuelve datos, reemplazamos o combinamos los fakes.
                // Aquí solo reemplazamos:
                this.notifications = data;
            },
            error: (error) => {
                console.error('Error al cargar notificaciones de la API. Mostrando datos de prueba.', error);
                // Si la API falla, puedes comentar la línea de arriba y descomentar la de abajo para
                // mostrar solo los datos fake como respaldo:
                // this.notifications = this.fakeNotifications;
            },
        });

        /*this._notificationsService.get_notifications().subscribe({
            next: (data) => {
                this.notifications = data;
            },
            error: (data) => {
                console.log(data);
            },
        });*/
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }


    initializeLanguage() {
        // 1. Establecer el idioma por defecto (fallback) por si todo lo demás falla
        this.translate.setDefaultLang('es');

        // 2. Intentar obtener el idioma guardado manualmente por el usuario en visitas previas
        const savedLang = localStorage.getItem('lang');

        // 3. Obtener el idioma del navegador/sistema
        // getBrowserLang() devuelve 'en', 'es', etc.
        const browserLang = this.translate.getBrowserLang();

        let finalLang: string;

        if (savedLang) {
            // Prioridad 1: Preferencia guardada manualmente
            finalLang = savedLang;
        } else if (browserLang && browserLang.match(/es|en/)) {
            // Prioridad 2: Idioma del sistema (si es inglés o español)
            finalLang = browserLang;
        } else {
            // Prioridad 3: Si el sistema es Chino, Francés, etc., poner Español por defecto
            finalLang = 'es';
        }

        // Aplicar el idioma
        this.translate.use(finalLang);
        this.currentLang = finalLang;
    }


    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, notification: Notification): string {
        return notification.viajeId;
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /*getNotificationSvgIcon(proceso: string): string {
        if (!proceso) {
            return 'heroicons_outline:bell';
        }

        switch (proceso.toLowerCase()) {
            case 'reasignación de viaje':
                return 'heroicons_outline:switch-horizontal';
            case 'viaje pagado':
                return 'heroicons_outline:currency-dollar';
            case 'nueva reservación':
                return 'heroicons_outline:check-circle';
            case 'cancelación de reservación':
                return 'heroicons_outline:x-circle';
            case 'asignación de conductor':
                return 'heroicons_outline:user';
            case 'reasignación de conductor':
                return 'heroicons_outline:refresh';
            default:
                return 'heroicons_outline:bell';
        }
    }

    getNotificationIconColorClass(proceso: string): string {
        if (!proceso) {
            return 'notification-icon-default';
        }

        switch (proceso.toLowerCase()) {
            case 'reasignación de viaje':
                return 'notification-icon-orange';
            case 'viaje pagado':
                return 'notification-icon-green';
            case 'nueva reservación':
                return 'notification-icon-blue';
            case 'cancelación de reservación':
                return 'notification-icon-red';
            case 'asignación de conductor':
                return 'notification-icon-light-blue';
            case 'reasignación de conductor':
                return 'notification-icon-deep-purple';
            default:
                return 'notification-icon-default';
        }
    }*/



    getNotificationSvgIcon(proceso: string): string {
        if (!proceso) {
            return 'heroicons_outline:bell';
        }

        switch (proceso.toLowerCase()) {
            case 'asignación de viaje':
                return 'heroicons_outline:credit-card';
            case 'cancelación de reservación':
                return 'heroicons_outline:x';
            case 'pago':
                return 'heroicons_outline:check';
            default:
                return 'heroicons_outline:information-circle';
        }
    }

    getNotificationIconColorClass(proceso: string): string {
        if (!proceso) {
            return 'notification-icon-default';
        }
        switch (proceso.toLowerCase()) {
            case 'asignación de viaje':
                return 'notification-icon-blue';
            case 'cancelación de reservación':
                return 'notification-icon-red';
            case 'pago':
                return 'notification-icon-green';
            default:
                return 'notification-icon-default';
        }
    }
}
